import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { MoreVertical } from "lucide-react";
import { useTranslationApp } from "../../../lib/i18next";
import { BOX_SHADOW_THEME } from "../../../utils/colors";
import { FEATURE_ADDON_BILLING_MENU } from "../../../i18n/keysTranslations";

export const ContainerFeatureAddonBillingRows = ({ children }) => (
  <Paper
    elevation={0}
    sx={{
      width: "100%",
      backgroundColor: "background.paper",
      boxShadow: BOX_SHADOW_THEME,
      border: "1px solid rgba(0, 0, 0, 0.08)",
      borderRadius: 3,
      overflow: "hidden",
    }}
  >
    {children}
  </Paper>
);

ContainerFeatureAddonBillingRows.propTypes = {
  children: PropTypes.node,
};

/** Shared layout for add-on rows in billing: icon, texts, actions and menu. */
const FeatureAddonBillingRow = ({
  Icon,
  title,
  badge = null,
  details = null,
  actions = null,
  menuItems = [],
  busy = false,
  menuDisabled = false,
  reserveMenu = false,
}) => {
  const { t } = useTranslationApp();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const items = menuItems.filter(Boolean);
  const menuLocked = busy || menuDisabled;

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: { xs: "wrap", sm: "nowrap" },
        alignItems: "center",
        columnGap: 2,
        rowGap: 1.5,
        px: 2,
        py: 1.75,
        borderTop: "1px solid rgba(0, 0, 0, 0.08)",
        "&:first-of-type": { borderTop: "none" },
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 4,
          backgroundColor: "rgba(0,0,0,0.05)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: "#111827",
        }}
      >
        <Icon size={22} color="#111827" strokeWidth={2} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
          {badge}
        </Box>
        {details}
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          gap: 1.5,
          flexShrink: 0,
          width: { xs: "100%", sm: "auto" },
          order: { xs: 2, sm: 0 },
        }}
      >
        {actions}
      </Box>
      {items.length > 0 ? (
        <>
          <IconButton
            aria-label={t(FEATURE_ADDON_BILLING_MENU)}
            disabled={menuLocked}
            onClick={(event) => setMenuAnchor(event.currentTarget)}
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              ml: { xs: "auto", sm: 0 },
              order: { xs: 1, sm: 0 },
            }}
          >
            {busy ? <CircularProgress size={18} /> : <MoreVertical size={18} />}
          </IconButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {items.map((item) => (
              <MenuItem
                key={item.key}
                disabled={menuLocked || item.disabled}
                onClick={() => {
                  setMenuAnchor(null);
                  item.onClick();
                }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : reserveMenu ? (
        <Box
          sx={{
            width: 34,
            height: 34,
            flexShrink: 0,
            display: { xs: "none", sm: "block" },
          }}
        />
      ) : null}
    </Box>
  );
};

FeatureAddonBillingRow.propTypes = {
  Icon: PropTypes.elementType.isRequired,
  title: PropTypes.node.isRequired,
  badge: PropTypes.node,
  details: PropTypes.node,
  actions: PropTypes.node,
  menuItems: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      onClick: PropTypes.func.isRequired,
      disabled: PropTypes.bool,
    })
  ),
  busy: PropTypes.bool,
  menuDisabled: PropTypes.bool,
  reserveMenu: PropTypes.bool,
};

export default FeatureAddonBillingRow;
